import nxModule from 'nxModule';
import * as ng from 'angular';
import templateUrl from './update-cif-branch.template.html';
import {BranchService} from "../../../service/branch.service";
import { CustomerSearchResult } from 'components/customer/profile/customer-search-result.types';
import {Branch} from "management/BranchTypes";
import {HttpService} from "shared/utils/httpService";
import {PageResult} from "tools/HttpTypes";
import {NxRouteService} from "routes/NxRouteService";
import ConfirmationTemplate from "shared/common/confirmationTemplate";
import {CommandService} from "shared/utils/command/command.types";
import Popup from "shared/common/popup";
import {RequestFormatter} from "shared/utils/requestFormatter.types";


type CifSelectionMode = 'file' | 'manual-input';

interface CustomerDetails {
  name: string;
  customerNumber: string;
  currentBranch: string;
  customerType: string;
  customerStatus: string;
}

type CustomerDescription = Partial<CustomerDetails> & Pick<CustomerDetails, 'customerNumber'>;

export class UpdateCifBranch {
  cifSelectionModes: {label: string, value: CifSelectionMode}[] = [
    {
      label: 'File',
      value: 'file'
    }, {
      label: 'Manual Input',
      value: 'manual-input'
    }
  ];

  branches: Branch[] = [];
  targetBranch: Branch | null = null;
  cifSelectionMode: CifSelectionMode | null = null;
  customersToBeUpdated: CustomerDescription[] = [];
  searchCifNumber: string = "";
  searchName: string = "";
  searchResults: CustomerSearchResult[] = [];
  innerFile: string = '';

  get file(): string {
    return this.innerFile;
  }

  set file(text: string) {
    this.innerFile = text;

    this.customersToBeUpdated = (text ?? '')
        .trim()
        .split('\n')
        .map(line => line.trim())
        .map(customerNumber => ({
            customerNumber,
        }));
  }

  constructor(private http: HttpService, private $scope: ng.IScope, private $route: NxRouteService,
              private branchService: BranchService, private confirmationTemplate: ConfirmationTemplate,
              private command: CommandService, private popup: Popup, private requestFormatter: RequestFormatter) {
  }

  async $onInit(): Promise<void> {
    this.branches = await this.branchService.toPromise();
  }

  onCifSelectionChanged(): void {
    this.customersToBeUpdated = [];
    this.clearSearch();
  }

  async searchCustomers(): Promise<void> {
    const cif = this.searchCifNumber.trim();
    const name = this.searchName.trim();

    if (!cif && !name) {
      this.searchResults = [];
      this.popup({text: `Enter a cif number or a name to search.`});
      return;
    }

    const searchCriteria = {
        customerNumber: cif || null,
        customerId: null,
        documentNumber: null,
        name: name || null,
        birthDate: null,
        productNumber: null,
        checkNumber: null,
        statuses: ['ACTIVE', 'PROSPECT'],
        customerTypes: ['INDIVIDUAL', 'CORPORATE'],
        registrationDate: null,
        // A cif is matched exactly, a name partially. Values are strings because
        // convertToQueryParams takes Record<string, string | string[] | null>.
        exactCustomerNumber: cif ? 'true' : null,
        pageSize: '50'
      };

    const data = await this.http.get<PageResult<CustomerSearchResult>>(
        `/customers${this.requestFormatter.convertToQueryParams(searchCriteria)}`,
        {nxLoaderText: 'Searching for customer...'}
        ).toPromise();

    this.searchResults = data?.result ?? [];

    if (this.searchResults.length === 0) {
      this.popup({text: `No customer found.`});
    }
  }

  selectCustomer(customer: CustomerSearchResult): void {
    if (this.checkIfExisting(customer.customerNumber)) {
      this.popup({
        text: `Cif number ${customer.customerNumber} is already included in the list.`
      });

      return;
    }

    this.customersToBeUpdated.push({
      ...customer,
      currentBranch: this.getBranchName(customer.branchId)
    });
    this.clearSearch();
  }

  clearSearch(): void {
    this.searchCifNumber = "";
    this.searchName = "";
    this.searchResults = [];
  }

  checkIfExisting(customerNumber: string): boolean {
      return this.customersToBeUpdated.find(c => c.customerNumber === customerNumber) !== undefined;
  }

  removeCustomer(index: number): void {
    this.customersToBeUpdated.splice(index, 1);
  }


  async execute(): Promise<void> {
    if(!this.targetBranch) {
        this.popup({text: `Target branch is required.`});
        return;
    }

    const customerNumbers = this.customersToBeUpdated.map(p => p.customerNumber);

    const payload = {
      targetBranchId: this.targetBranch.id,
      customerNumbers: customerNumbers
    };

    const customerNames = this.customersToBeUpdated
      .map(c => c.name ? `${c.customerNumber} - ${c.name}` : c.customerNumber)
      .join(', ');

    const confirmed = await this.confirmationTemplate({
      question: 'These cif numbers will be moved to another branch. Do you wish to proceed?',
      details: [
        {label: 'Customers', description: customerNames},
        {label: 'Branch name', description: this.targetBranch.name}
      ]
    });

    if(confirmed) {
      await this.command.execute('UpdateCifBranch', payload).toPromise();
      await this.popup({
        text: `Mother branch of customer(s) successfully changed to ${this.targetBranch.name}`,
      });
      this.$route.reload();
    }
  }

  downloadSample(): void {
    const sampleFileUrl = window.URL.createObjectURL(new Blob(["122148\n122150"],
      {type: 'text/plain'})
    );

    const a = document.createElement('a');
    a.href = sampleFileUrl;
    a.download = "sample-update-cif-branch.txt";
    a.click();
  }

  getBranchLabel(branch: Branch): string {
    return `${branch.name} : ${branch.systemDate}`;
  }

  getBranchName(branchId: number): string | undefined {
    return this.branches.find(b => b.id === branchId)?.name;
  }
}

nxModule.component('updateCifBranch', {
  templateUrl,
  controller: UpdateCifBranch
});
